
import { useAuth } from "../contexts/AuthContext";
import { useState } from "react";
import TaskHeader from "../components/tasks/TaskHeader";
import TaskFilters from "../components/tasks/TaskFilters";
import TaskList from "../components/tasks/TaskList";
import TaskDetailDialog from "../components/tasks/TaskDetailDialog";
import NewTaskDialog from "../components/tasks/NewTaskDialog";
import { useTasks } from "../hooks/useTasks";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, ListFilter } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Tasks = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "board" || user?.role === "teacher";
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false);
  
  const {
    tasks,
    filters,
    setFilters,
    viewMode,
    setViewMode,
    selectedTask,
    isTaskDetailOpen,
    setIsTaskDetailOpen,
    handleTaskStatusChange,
    handleTaskUpdate,
    handleViewTask,
    handleAddComment,
    handleRequestChange,
    handleChangeRequestResponse,
    pendingRequestsCount,
    addTask
  } = useTasks(user?.id);

  const handleNewTask = () => {
    setIsNewTaskOpen(true);
  };

  const handleViewModeChange = (newMode: "my" | "all") => {
    setViewMode(newMode);
  };

  // Get tasks with pending requests
  const tasksWithPendingRequests = tasks.filter(task => 
    task.changeRequests?.some(req => req.status === "pending")
  );

  return (
    <div className="space-y-6">
      <TaskHeader 
        isBoard={isAdmin}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onNewTask={handleNewTask}
        pendingRequestsCount={pendingRequestsCount}
      />

      {isAdmin && viewMode === "all" && (
        <Tabs defaultValue="all-tasks" className="w-full">
          <TabsList>
            <TabsTrigger value="all-tasks">
              All Tasks
            </TabsTrigger>
            <TabsTrigger value="pending-requests" className={pendingRequestsCount > 0 ? "text-amber-600 font-medium" : ""}>
              Change Requests ({pendingRequestsCount})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all-tasks" className="mt-4">
            {pendingRequestsCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center gap-2 mb-4">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <p className="text-sm text-amber-800">
                  There are {pendingRequestsCount} pending change request{pendingRequestsCount > 1 ? 's' : ''} across all tasks
                </p>
              </div>
            )}

            <div className="space-y-4">
              <TaskFilters filters={filters} onFilterChange={setFilters} />
              
              <TaskList 
                tasks={tasks}
                onStatusChange={handleTaskStatusChange}
                onView={handleViewTask}
                isAdminView={isAdmin && viewMode === "all"}
                currentUserId={user?.id}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="pending-requests" className="mt-4">
            {pendingRequestsCount > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ListFilter className="h-5 w-5 text-amber-500" />
                  <h3 className="text-lg font-medium">Tasks Requiring Attention</h3>
                </div>
                
                <TaskList 
                  tasks={tasksWithPendingRequests}
                  onStatusChange={handleTaskStatusChange}
                  onView={handleViewTask}
                  isAdminView={true}
                  currentUserId={user?.id}
                />
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-6 text-center">
                <p className="text-muted-foreground">No pending change requests</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
      
      {(!isAdmin || viewMode !== "all") && (
        <div className="space-y-4">
          <TaskFilters filters={filters} onFilterChange={setFilters} />
          
          <TaskList 
            tasks={tasks}
            onStatusChange={handleTaskStatusChange}
            onView={handleViewTask}
            isAdminView={isAdmin && viewMode === "all"}
            currentUserId={user?.id}
          />
        </div>
      )}
      
      <TaskDetailDialog
        isOpen={isTaskDetailOpen}
        onOpenChange={setIsTaskDetailOpen}
        task={selectedTask}
        isAdmin={isAdmin}
        isAdminView={isAdmin && viewMode === "all"}
        currentUserId={user?.id}
        currentUserName={user?.name}
        currentUserAvatar={user?.avatar}
        onUpdateTask={handleTaskUpdate}
        onAddComment={handleAddComment}
        onRequestChange={handleRequestChange}
        onChangeRequestResponse={handleChangeRequestResponse}
      />

      <NewTaskDialog 
        isOpen={isNewTaskOpen}
        onClose={() => setIsNewTaskOpen(false)}
        onSave={addTask}
      />
    </div>
  );
};

export default Tasks;
